import { AddIcon, DeleteIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box, Button, Checkbox, Flex, Grid, Heading,
  Icon,
  IconButton, Image, Input, Spinner, Text, Textarea,
  Tooltip
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { BiImageAdd } from 'react-icons/bi';
import { MdDelete } from 'react-icons/md';
import { useUploadManualMutation } from 'store/aiHub/aiGenerationApi';

const ProductUploadForm = forwardRef(function ProductUploadForm(
  { initialValues, isLoading, onSubmit, onFormUpdate },
  ref
) {

  const safeInitialValues = (initialValues && !Array.isArray(initialValues))
    ? initialValues
    : {
      brandName: '',
      description: '',
      logo_url: '',
      assets: [],
    };

  const [assets, setAssets] = useState([]);
  const [logoUrl, setLogoUrl] = useState('');
  const fileInputRef = useRef();
  const logoInputRef = useRef();

  const [uploadManual, { isLoading: isUploadManual }] = useUploadManualMutation();

  const formik = useFormik({
    initialValues: safeInitialValues,
    enableReinitialize: true,
    onSubmit: values => {
      onSubmit?.({ ...values, logo_url: logoUrl, assets });
    },
  });

  useImperativeHandle(ref, () => ({
    submitForm: formik.submitForm,
    getValues: () => ({ ...formik.values, logo_url: logoUrl, assets }),
    isValid: formik.isValid,
  }));

  useEffect(() => {
    if (Array.isArray(initialValues?.assets)) {
      const preparedAssets = initialValues.assets.map(asset => ({
        ...asset,
        selected: asset.selected ?? true,
        type: asset.type ?? (asset.url?.includes('.mp4') ? 'video' : 'image'),
      }));
      setAssets(preparedAssets);
    }

    setLogoUrl(initialValues?.logo_url || '');
  }, [initialValues]);

  useEffect(() => {
    onFormUpdate?.({ ...formik.values, logo_url: logoUrl, assets });
  }, [formik.values.brandName, logoUrl, assets]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    fileInputRef.current.value = null;
    if (files.length === 0) return;

    const newAssets = files.map(file => ({
      url: URL.createObjectURL(file),
      selected: true,
      file,
      uploading: true,
      type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'other',
    }));

    setAssets(prev => [...prev, ...newAssets]);

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    try {
      const uploadResponse = await uploadManual(formData).unwrap();
      const realUrls = uploadResponse.urls;

      const updatedNewAssets = newAssets.map((asset, idx) => ({
        ...asset,
        url: realUrls[idx],
        uploading: false,
      }));

      setAssets(prev => {
        const filtered = prev.filter(asset => !newAssets.includes(asset));
        return [...filtered, ...updatedNewAssets];
      });

    } catch (error) {
      console.error("Error uploading assets:", error);
      setAssets(prev => prev.map(asset => (
        newAssets.includes(asset) ? { ...asset, uploading: false } : asset
      )));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    logoInputRef.current.value = null;
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    setLogoUrl(tempUrl);

    const formData = new FormData();
    formData.append('files', file);

    try {
      const uploadResponse = await uploadManual(formData).unwrap();
      const firstUrl = uploadResponse.urls?.[0];
      if (!firstUrl) return;

      setLogoUrl(firstUrl); 

    } catch (error) {
      //console.error("Error uploading logo:", error);
      setLogoUrl(''); // Clear the logo if upload fails
    }
  };


  const toggleSelectAll = value => {
    setAssets(assets.map(asset => ({ ...asset, selected: value })));
  };

  const toggleAssetSelection = index => {
    const updated = [...assets];
    updated[index].selected = !updated[index].selected;
    setAssets(updated);
  };

  const removeAsset = index => {
    const updated = [...assets];
    updated.splice(index, 1);
    setAssets(updated);
  };

  if (isLoading) {
    return (
    <Flex direction={"column"} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
      <Image src="/Ai_ImageLoader.gif" alt="generating..." boxSize="200px" objectFit="contain" width="100%" />
      <Flex direction={"column"} alignItems={"center"} gap={2}>
        <Text fontWeight={"bold"} fontSize={"14px"}>
          Scraping website data...
        </Text>
        <Text fontSize={"14px"}>Fetching media & product details</Text>
      </Flex>
    </Flex>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Flex direction="column" gap={6} p={2} width="640px">
        <Heading fontSize="2xl">Add media and product details</Heading>

        {/* Brand/Product Name and Logo Upload */}
        <Flex gap={4} alignItems="center">
          <Flex flex={0.6} gap={4}>
            <Box flex="1">
              <Text fontWeight="bold" mb={1}>Brand/Product name</Text>
              <Input
                name="brandName"
                size="sm"
                borderRadius="md"
                value={formik.values.brandName}
                onChange={formik.handleChange}
                placeholder="Enter product name"
              />
            </Box>

            <Box>
              <Text fontWeight="bold" mb={1}>Logo</Text>
              <Box
                w="35px"
                h="35px"
                borderRadius="md"
                border={!logoUrl ? '1px' : 'none'}
                borderColor={!logoUrl ? 'gray.100' : 'none'}
                position="relative"
                overflow="hidden"
                onClick={() => !logoUrl && logoInputRef.current.click()}
                cursor={!logoUrl ? 'pointer' : 'default'}
                _hover={{ '.overlay': { opacity: 1 } }}
              >
                {logoUrl ? (
                  <>
                    <Image src={logoUrl} alt="logo" w="100%" h="100%" objectFit="cover" />
                    <Flex
                      className="overlay"
                      position="absolute"
                      top="0"
                      left="0"
                      w="100%"
                      h="100%"
                      align="center"
                      justify="center"
                      bg="rgba(255,255,255,0.6)"
                      opacity={0}
                      cursor="pointer"
                      transition="opacity 0.2s ease"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLogoUrl('');
                      }}
                    >
                      <Icon as={MdDelete} color="gray.800" boxSize={4} />
                    </Flex>
                  </>
                ) : (
                  <Flex align="center" justify="center" w="100%" h="100%">
                    <Icon as={BiImageAdd} boxSize={4} />
                  </Flex>
                )}
                <Input
                  type="file"
                  ref={logoInputRef}
                  accept="image/*"
                  display="none"
                  onChange={handleLogoUpload}
                />
              </Box>
            </Box>
          </Flex>
        </Flex>

        {/* Product Description */}
        <Box>
          <Text fontWeight="bold" mb={1}>Product description</Text>
          <Textarea
            name="description"
            size="sm"
            fontSize={'12px'}
            borderRadius="md"
            value={formik.values.description}
            onChange={formik.handleChange}
            placeholder="Describe your product"
            rows={8}
          />
        </Box>

        {/* Assets Section */}
        <Box>
          <Flex gap={1} direction={'column'} mb={2}>
            <Flex gap={2} alignItems={'center'}>
              <Text fontWeight="bold">Assets</Text>
              <Tooltip
              borderRadius={5}
              label={
                <Box fontSize="12px" p={'5px'}>
                  <Text>Confirm the assets you want to use. Higher-quality clips and images result in better results.</Text>
                </Box>
              }
              fontSize="md"
            >
              <InfoOutlineIcon pointerEvents="all" />
            </Tooltip>
            </Flex>
            <Text fontSize={'12px'}>Please select at least 3 assets to proceed.</Text>
          </Flex>
          <Box maxH="260px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
              {/* Add Icon */}
              <Box
                w="100%"
                h="120px"
                border="1px dashed gray"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={() => fileInputRef.current.click()}
              >
                <Flex direction="column" alignItems="center" gap={2}>
                  <Icon as={AddIcon} boxSize={6} />
                  <Text>Upload</Text>
                </Flex>
              </Box>

              {/* Uploaded Assets */}
              {assets?.map((asset, idx) => (
                <Box
                  key={idx}
                  w="100%"
                  h="120px"
                  position="relative"
                  borderRadius="md"
                  overflow="hidden"
                  border="1px solid"
                  borderColor={asset.selected ? "brand.800" : "gray.200"}
                  _hover={{ '.delete-btn': { display: 'flex', bg: 'gray.100' } }}
                >
                  {asset.uploading && (
                    <Flex
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      align="center"
                      justify="center"
                      bg="rgba(255, 255, 255, 0.6)"
                      zIndex={3}
                    >
                      <Spinner size="md" thickness="3px" speed="0.65s" />
                    </Flex>
                  )}

                  <Checkbox
                    isChecked={asset.selected}
                    onChange={() => toggleAssetSelection(idx)}
                    position="absolute"
                    top="6px"
                    left="6px"
                    size="md"
                    bg="gray.800"
                    colorScheme='brand'
                    zIndex={2}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size="xs"
                    position="absolute"
                    top="4px"
                    right="4px"
                    zIndex={2}
                    aria-label="Remove"
                    onClick={() => removeAsset(idx)}
                    className="delete-btn"
                    display="none"
                  />
                  {asset.type === 'image' ? (
                    <Image src={asset.url} w="100%" h="100%" objectFit="cover" />
                  ) : (
                    <video src={asset.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                </Box>
              ))}

              <Input
                type="file"
                ref={fileInputRef}
                accept="image/*,video/*"
                multiple
                display="none"
                onChange={handleFileUpload}
              />
            </Grid>
          </Box>

          {/* Actions */}
          {assets?.length > 0 && (
            <Flex mt={3} gap={3} wrap="wrap">
              <Button size="xs" onClick={() => toggleSelectAll(true)}>Select all</Button>
              <Button size="xs" onClick={() => toggleSelectAll(false)}>Unselect all</Button>
              <Button
                size="xs"
                colorScheme="red"
                onClick={() => setAssets(assets.filter(a => !a.selected))}
                isDisabled={assets?.every(a => !a.selected)}
              >
                Delete select
              </Button>
            </Flex>
          )}
        </Box>
      </Flex>
    </form>
  );
});

export default ProductUploadForm;
